/**
 * Bandeau « Nouvelle campagne nationale » sur la page d'accueil.
 *
 * Met en avant la campagne courante de la Sécurité routière (voir
 * CURRENT_CAMPAIGN dans /data/officialVideos.js). Affiche un teaser éditorial
 * + lecteur YouTube intégré (mode no-cookie, lazy load via "click-to-play")
 * + CTA vers la page édito dédiée.
 *
 * Le bandeau peut être refermé par l'utilisateur ; la fermeture est
 * persistante par campagne (localStorage) pour qu'il n'ait pas à le refermer
 * à chaque visite.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowRight, X, Sparkles, Newspaper, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CURRENT_CAMPAIGN } from '../data/officialVideos';

const LS_KEY = `c2r:dismiss-campaign:${CURRENT_CAMPAIGN.slug}`;

export default function CampaignBanner() {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(LS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [playing, setPlaying] = useState(false);

  if (dismissed) return null;

  const handleDismiss = () => {
    try { window.localStorage.setItem(LS_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  const thumbnail = `https://i.ytimg.com/vi/${CURRENT_CAMPAIGN.heroVideoId}/maxresdefault.jpg`;
  const embedSrc = `https://www.youtube-nocookie.com/embed/${CURRENT_CAMPAIGN.heroVideoId}?rel=0&modestbranding=1&autoplay=1`;

  return (
    <section
      className="relative overflow-hidden rounded-2xl border bg-card shadow-sm"
      data-testid="campaign-banner"
      aria-label="Nouvelle campagne nationale Sécurité routière 2026"
    >
      {/* Texture décorative subtile */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(-45deg, transparent 0 14px, hsl(var(--foreground)) 14px 15px)',
        }}
      />

      {/* Bouton fermer */}
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-background/70 hover:bg-background border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
        aria-label="Fermer le bandeau"
        data-testid="campaign-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="relative grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-0">
        {/* COLONNE TEXTE */}
        <div className="p-4 sm:p-5 lg:p-6 flex flex-col justify-center">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge className="bg-[hsl(var(--primary))] text-white border-none text-[10px] gap-1">
              <Sparkles className="w-3 h-3" /> Nouvelle campagne
            </Badge>
            <span className="text-[10px] uppercase tracking-[0.18em] font-black text-[hsl(var(--primary))]">
              Sécurité routière · 2026
            </span>
          </div>

          <h2
            className="text-xl sm:text-2xl lg:text-[26px] font-extrabold tracking-tight leading-[1.15] mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="campaign-banner-title"
          >
            {CURRENT_CAMPAIGN.title}
          </h2>

          <p className="text-xs sm:text-sm text-foreground/70 leading-relaxed mb-3 max-w-xl line-clamp-2">
            {CURRENT_CAMPAIGN.subtitle}
          </p>

          <div className="flex flex-wrap items-center gap-1.5 mb-3 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border bg-background/60">
              <Clock className="w-2.5 h-2.5" /> {CURRENT_CAMPAIGN.heroVideoDuration}
            </span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border bg-background/60">
              <Newspaper className="w-2.5 h-2.5" /> Décryptage Code 2 Roues
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button asChild size="sm" data-testid="campaign-banner-cta-edito">
              <Link to={`/${CURRENT_CAMPAIGN.slug}`}>
                Lire le décryptage <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Link>
            </Button>
            {!playing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPlaying(true)}
                data-testid="campaign-banner-cta-play"
              >
                <Play className="w-3.5 h-3.5 mr-1 fill-current" /> Voir le film
              </Button>
            )}
          </div>
        </div>

        {/* COLONNE VIDÉO */}
        <div className="relative aspect-video lg:aspect-auto lg:min-h-[200px] bg-black overflow-hidden lg:rounded-l-none">
          {playing ? (
            <iframe
              src={embedSrc}
              title={CURRENT_CAMPAIGN.title}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              data-testid="campaign-banner-iframe"
            />
          ) : (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              className="absolute inset-0 w-full h-full group focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--primary))]"
              aria-label={`Lire la vidéo : ${CURRENT_CAMPAIGN.title}`}
              data-testid="campaign-banner-poster"
            >
              <img
                src={thumbnail}
                alt={CURRENT_CAMPAIGN.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 transition-opacity"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                  <Play className="w-7 h-7 sm:w-8 sm:h-8 text-white fill-white ml-1" />
                </div>
              </div>
              <div className="absolute bottom-3 left-4 right-4 text-left">
                <div className="text-[10px] font-black uppercase tracking-widest text-white/80 mb-0.5">
                  Film officiel · Sécurité routière
                </div>
                <div className="text-xs sm:text-sm font-bold text-white drop-shadow line-clamp-1">
                  Réalisation : {CURRENT_CAMPAIGN.directors}
                </div>
              </div>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
